import { useEffect, useRef, useState, type RefObject } from 'react';
import { Icon } from './icons.js';
export interface MenuAction {
  label: string;
  icon?: string;
  run: () => void;
  disabled?: boolean;
}
/** Même menu par bouton, clic droit et Maj+F10. Les champs gardent leur menu natif. */
export function ActionMenu({
  label,
  icon,
  actions,
  target,
  align = 'end',
}: {
  label: string;
  icon?: string;
  align?: 'start' | 'end';
  actions: readonly MenuAction[];
  target: RefObject<HTMLElement | null>;
}) {
  const [open, setOpen] = useState(false);
  const root = useRef<HTMLDivElement>(null);
  const trigger = useRef<HTMLButtonElement>(null);
  const returnTo = useRef<HTMLElement | null>(null);
  useEffect(() => {
    const el = target.current;
    if (!el) return;
    const context = (event: MouseEvent | KeyboardEvent) => {
      if (
        event.target instanceof HTMLElement &&
        event.target.closest('input,textarea,select,[contenteditable="true"]')
      )
        return;
      if (
        event instanceof KeyboardEvent &&
        !(event.key === 'ContextMenu' || (event.shiftKey && event.key === 'F10'))
      )
        return;
      event.preventDefault();
      event.stopPropagation();
      returnTo.current =
        document.activeElement instanceof HTMLElement ? document.activeElement : null;
      setOpen(true);
    };
    el.addEventListener('contextmenu', context);
    el.addEventListener('keydown', context);
    return () => {
      el.removeEventListener('contextmenu', context);
      el.removeEventListener('keydown', context);
    };
  }, [target]);
  useEffect(() => {
    if (!open) return;
    root.current?.querySelector<HTMLButtonElement>('[role="menuitem"]:not(:disabled)')?.focus();
    const close = (event: PointerEvent) => {
      if (event.target instanceof Node && !root.current?.contains(event.target)) setOpen(false);
    };
    document.addEventListener('pointerdown', close);
    return () => document.removeEventListener('pointerdown', close);
  }, [open]);
  const close = () => {
    setOpen(false);
    (returnTo.current ?? trigger.current)?.focus();
  };
  return (
    <div
      ref={root}
      className="ndrc-action-menu"
      onKeyDown={(event) => {
        if (event.key === 'Escape') {
          event.stopPropagation();
          close();
        }
        if (event.key === 'Tab') setOpen(false);
        if (['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) {
          event.preventDefault();
          const items = Array.from(
            root.current?.querySelectorAll<HTMLButtonElement>('[role="menuitem"]:not(:disabled)') ??
              [],
          );
          const current = items.indexOf(document.activeElement as HTMLButtonElement);
          const next =
            event.key === 'Home'
              ? 0
              : event.key === 'End'
                ? items.length - 1
                : (current + (event.key === 'ArrowUp' ? -1 : 1) + items.length) % items.length;
          items[next]?.focus();
        }
      }}
    >
      <button
        ref={trigger}
        type="button"
        className={icon ? 'ndrc-action-trigger--icon' : undefined}
        aria-label={label}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => {
          returnTo.current = trigger.current;
          setOpen(!open);
        }}
      >
        {icon ? <Icon name={icon} size={19} /> : label}
      </button>
      {icon && !open ? <span className="ndrc-action-tooltip">{label}</span> : null}
      {open ? (
        <div
          role="menu"
          aria-label={label}
          className="ndrc-action-options"
          style={align === 'start' ? { left: 0, right: 'auto' } : undefined}
        >
          {actions.map((action) => (
            <button
              key={action.label}
              type="button"
              role="menuitem"
              disabled={action.disabled}
              onClick={() => {
                close();
                action.run();
              }}
            >
              {action.icon ? <Icon name={action.icon} size={17} /> : null}
              <span>{action.label}</span>
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}
